import { ColorValue, Platform, StyleSheet, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

import { AddonTypes } from 'store/Orders/enums';
import { useItemAddonDisplayName } from 'util/hooks/useItemAddonDisplayName';
import { Text } from 'components';

import type { ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import type { OrderItem, PizzaAddon } from 'store/Orders/types';

type CommonProps = {
  fontSize?: number;
  addonsDisplayNames: OrderItem['addons_display_names'];
};

type ColumnProps = {
  labelKey?: string;
  labelLeft?: ReactNode;
  items: string[];
  hasHorizontalSibling?: boolean;
  style?: StyleProp<ViewStyle>;
} & CommonProps;

const Column = (props: ColumnProps) => {
  const {
    labelKey,
    items,
    labelLeft,
    hasHorizontalSibling,
    fontSize = 14,
    style,
    addonsDisplayNames,
  } = props;

  const { getItemAddonDisplayName } = useItemAddonDisplayName();

  return (
    <View
      style={[
        {
          maxWidth: '100%',
        },
        hasHorizontalSibling
          ? {
              flexShrink: 1,
              minWidth: '30%',
              flexBasis: 'auto',
              justifyContent: 'space-between',
              alignSelf: 'flex-start',
            }
          : {
              // flex: 1,
            },
        style,
      ]}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          maxWidth: '100%',
        }}
      >
        <View>{labelLeft}</View>
        {!!labelKey && (
          <Text
            size={fontSize * 0.9}
            color={'#dd8c33'}
            font={'bold'}
            id={labelKey}
            numberOfLines={1}
            style={{
              flexShrink: 1,
            }}
          />
        )}
      </View>
      <View>
        {items.length > 0 ? (
          items.map((item, index) => (
            <View
              style={{
                flexDirection: 'row',
                flexShrink: 1,
                marginBottom: 5,
              }}
              // Important: do not use addon name as `key`, especially as long as this component is nested in any recycler list.
              // Addons can repeat (["extra sauce", "extra cheese", "extra sauce"]) which may cause rendering issues
              // and mess with recycling resulting in incorrect data being displayed.
              //
              // https://shopify.github.io/flash-list/docs/fundamentals/performant-components#remove-key-prop
              key={index}
            >
              <MaterialCommunityIcons
                color={'#dd8c33'}
                name={'circle'}
                style={{
                  marginTop: fontSize * 0.5,
                  marginRight: 3,
                  marginLeft: 3,
                }}
                size={fontSize * 0.5}
              />
              {/* This particular style ({ flexDirection: 'row', flexWrap: 'wrap' }) is text wrapping magic  */}
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  flexShrink: 1,
                }}
              >
                <Text
                  color={'#333'}
                  size={fontSize}
                  font={'medium'}
                  textBreakStrategy={'simple'}
                  android_hyphenationFrequency={'normal'}
                  style={Platform.OS === 'web' && { wordBreak: 'break-word' }}
                >
                  {getItemAddonDisplayName(item, addonsDisplayNames) ?? item}
                </Text>
              </View>
            </View>
          ))
        ) : (
          <MaterialCommunityIcons name={'close'} size={18} color={'#dd8c33'} />
        )}
      </View>
    </View>
  );
};

const PlainAddons = (props: { addons: string[] } & CommonProps) => {
  const { addons, fontSize, addonsDisplayNames } = props;
  if (addons.length === 0) {
    return null;
  }
  return (
    <Column
      addonsDisplayNames={addonsDisplayNames}
      items={addons}
      fontSize={fontSize}
    />
  );
};

const Circle = (props: { children?: ReactNode; color: ColorValue }) => {
  const { children, color } = props;
  return (
    <View
      style={[
        styles.circle,
        {
          backgroundColor: color,
        },
      ]}
    >
      {children}
    </View>
  );
};

const CircleFill = (props: { position: 'left' | 'right' }) => {
  const { position } = props;

  return (
    <View
      style={[
        styles.circleFill,
        {
          left: position === 'right' ? 12 : -13,
        },
      ]}
    />
  );
};

const PizzaAddons = (
  props: {
    addons: OrderItem['addons'];
    shouldPizzaAddonVerticalOnly?: boolean;
  } & CommonProps,
) => {
  const { addons, fontSize, shouldPizzaAddonVerticalOnly, addonsDisplayNames } =
    props;

  if (Array.isArray(addons) && typeof addons[0] === 'string') {
    return (
      <PlainAddons
        addonsDisplayNames={addonsDisplayNames}
        addons={addons}
        fontSize={fontSize}
      />
    );
  }

  const pizzaAddons = addons as PizzaAddon;

  return (
    <View
      style={{
        flexWrap: 'wrap',
        marginTop: 12,
        flexGrow: 1,
        flexShrink: 1,
        gap: 4,
      }}
    >
      {!!pizzaAddons.whole?.length && (
        <Column
          addonsDisplayNames={addonsDisplayNames}
          items={pizzaAddons.whole}
          labelKey={'chef.item.addons.whole'}
          labelLeft={<Circle color={'#d8d8d8'} />}
          fontSize={fontSize}
        />
      )}
      <View
        style={{
          flexDirection: shouldPizzaAddonVerticalOnly ? 'column' : 'row',
          gap: 4,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          maxWidth: '100%',
        }}
      >
        {!!pizzaAddons.left?.length && (
          <>
            <Column
              addonsDisplayNames={addonsDisplayNames}
              hasHorizontalSibling={
                !shouldPizzaAddonVerticalOnly && !!pizzaAddons.right?.length
              }
              items={pizzaAddons.left}
              labelKey={'chef.item.addons.left'}
              labelLeft={
                <Circle color={'#fff'}>
                  <CircleFill position={'left'} />
                </Circle>
              }
              fontSize={fontSize}
            />
          </>
        )}
        {!!pizzaAddons.right?.length && (
          <Column
            addonsDisplayNames={addonsDisplayNames}
            hasHorizontalSibling={
              !shouldPizzaAddonVerticalOnly && !!pizzaAddons.left?.length
            }
            items={pizzaAddons.right}
            labelKey={'chef.item.addons.right'}
            labelLeft={
              <Circle color={'#fff'}>
                <CircleFill position={'right'} />
              </Circle>
            }
            fontSize={fontSize}
          />
        )}
      </View>
    </View>
  );
};

export interface AddonsProps extends Pick<OrderItem, 'addons'> {
  addonsType?: OrderItem['addons_type'];
  addonsDisplayNames: OrderItem['addons_display_names'];
  fontSize?: number;
  shouldPizzaAddonVerticalOnly?: boolean;
}

const Addons = (props: AddonsProps) => {
  const {
    addonsType,
    addons,
    fontSize,
    addonsDisplayNames,
    shouldPizzaAddonVerticalOnly,
  } = props;

  switch (addonsType) {
    case AddonTypes.PIZZA: {
      return (
        <PizzaAddons
          addonsDisplayNames={addonsDisplayNames}
          addons={addons}
          fontSize={fontSize}
          shouldPizzaAddonVerticalOnly={shouldPizzaAddonVerticalOnly}
        />
      );
    }
    default: {
      return (
        <PlainAddons
          addonsDisplayNames={addonsDisplayNames}
          addons={addons as string[]}
          fontSize={fontSize}
        />
      );
    }
  }
};

const styles = StyleSheet.create({
  circle: {
    borderColor: '#a6a6a6',
    borderRadius: 13,
    borderWidth: 1,
    height: 25,
    marginRight: 5,
    overflow: 'hidden',
    width: 25,
  },
  circleFill: {
    backgroundColor: '#d8d8d8',
    borderColor: '#a6a6a6',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 25,
    width: 25,
  },
});

export default Addons;
